import type { ElementType } from "react"

import { cn } from "~/lib/utils"
import type { Audience } from "~/data/audiences"
import { IconChip } from "./SectionKit"

// The look of an audience on the site: its hero art, the scrim that keeps text
// readable over it, and the icon + name "tag". Shared by the /solutions heroes
// (routes/solutions.tsx) and the audience cards on the landing page
// (SolutionsTeaser), so the two can't drift apart.

// How the art behaves, from `introArtTone` in data/audiences.ts:
//  - dark:      full-bleed dark art, text on it goes light
//  - pale:      full-bleed light art, text stays dark
//  - otherwise: transparent art that bleeds in from the bottom of a light card
export function artKind(audience: Audience) {
  const dark = audience.introArt != null && audience.introArtTone === "dark"
  const fullBleed =
    dark || (audience.introArt != null && audience.introArtTone === "pale")
  return { dark, fullBleed }
}

// The art image, plus (for full-bleed art) a soft pool of the card's own tone
// behind the centred text. Full-bleed art is busy in places (the data glow, the
// pins), so the scrim keeps text readable without dimming the whole
// illustration. Render inside a `relative overflow-hidden` parent; add the
// `group` class to that parent for the hover zoom.
export function AudienceArtLayers({ audience }: { audience: Audience }) {
  if (!audience.introArt) return null
  const { dark, fullBleed } = artKind(audience)
  return (
    <>
      <img
        src={audience.introArt}
        alt=""
        loading="lazy"
        decoding="async"
        className={cn(
          "pointer-events-none absolute",
          fullBleed
            ? "inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
            : "inset-x-0 bottom-0 h-auto w-full",
        )}
      />
      {fullBleed ? (
        <div
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute inset-0",
            dark
              ? "bg-[radial-gradient(ellipse_at_center,rgba(0,41,15,0.82)_0%,rgba(0,41,15,0.4)_60%,rgba(0,41,15,0)_100%)]"
              : "bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.78)_0%,rgba(255,255,255,0.4)_60%,rgba(255,255,255,0)_100%)]",
          )}
        />
      ) : null}
    </>
  )
}

// The audience name as a label: a pill with its icon. `as` picks the element
// (the section <h2> on /solutions, an <h3> on a landing card).
export function AudienceTag({
  audience,
  as: Tag = "h2",
  id,
  size = "md",
  tone,
}: {
  audience: Audience
  as?: ElementType
  id?: string
  size?: "sm" | "md" | "lg"
  // Force the dark or light pill (default: follows the art, see artKind)
  tone?: "light" | "dark"
}) {
  const dark = tone ? tone === "dark" : artKind(audience).dark
  return (
    <Tag
      id={id}
      className={cn(
        "inline-flex items-center rounded-full border font-sans font-semibold tracking-tight",
        size === "lg" && "gap-3 py-2 pl-2 pr-6 text-[22px] sm:text-[26px]",
        size === "md" && "gap-2.5 py-1.5 pl-1.5 pr-5 text-[18px] sm:text-[20px]",
        size === "sm" && "gap-2 py-1 pl-1 pr-4 text-[15px]",
        dark
          ? "border-paper/30 bg-forest/60 text-paper backdrop-blur-sm"
          : "border-forest/15 bg-paper/85 text-forest",
      )}
    >
      <IconChip
        icon={audience.icon}
        className={cn(
          "rounded-full",
          size === "lg" ? "h-10 w-10" : size === "md" ? "h-8 w-8" : "h-7 w-7",
          dark && "ring-1 ring-lime/30",
        )}
      />
      {audience.name}
    </Tag>
  )
}
